import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import type { ThemePreference } from '../types';

interface ThemeContextType {
  theme: ThemePreference;
  toggleTheme: () => Promise<void>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [theme, setTheme] = useState<ThemePreference>('light');

  useEffect(() => {
    if (user?.theme_preference) {
      setTheme(user.theme_preference);
      applyTheme(user.theme_preference);
    }
  }, [user]);

  const applyTheme = (newTheme: ThemePreference) => {
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const toggleTheme = async () => {
    if (!user) return;

    const newTheme: ThemePreference = theme === 'light' ? 'dark' : 'light';
    
    try {
      const { error } = await supabase
        .from('users')
        .update({ theme_preference: newTheme })
        .eq('id', user.id);

      if (error) throw error;

      setTheme(newTheme);
      applyTheme(newTheme);
    } catch (error) {
      console.error('Error updating theme:', error);
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};